import produce from 'immer';
import { ICommentsInitialState, types } from './types';
import { types as storeType } from '../refreshToken/types';

const initialState: ICommentsInitialState = {
    selectedComments: [],
};

const reducer = (state: ICommentsInitialState = initialState, action: any) => {
    const { type, payload } = action;

    // Helper function to recursively find and update a comment at any depth
    const findAndUpdateComment = (comments: any[], targetId: string, updater: (comment: any) => void): boolean => {
        for (let i = 0; i < comments.length; i++) {
            if (comments[i]._id === targetId) {
                updater(comments[i]);
                return true;
            }
            if (comments[i].replies && comments[i].replies.length > 0) {
                if (findAndUpdateComment(comments[i].replies, targetId, updater)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Helper function to recursively find and remove a comment at any depth
    const findAndRemoveComment = (comments: any[], targetId: string): boolean => {
        for (let i = 0; i < comments.length; i++) {
            if (comments[i]._id === targetId) {
                comments.splice(i, 1);
                return true;
            }
            if (comments[i].replies && comments[i].replies.length > 0) {
                if (findAndRemoveComment(comments[i].replies, targetId)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Helper function to recursively find and add a reply to a comment at any depth
    const findAndAddReply = (comments: any[], parentId: string, newReply: any): boolean => {
        for (let i = 0; i < comments.length; i++) {
            if (comments[i]._id === parentId) {
                if (!comments[i].replies) {
                    comments[i].replies = [];
                }
                comments[i].replies.push(newReply);
                return true;
            }
            if (comments[i].replies && comments[i].replies.length > 0) {
                if (findAndAddReply(comments[i].replies, parentId, newReply)) {
                    return true;
                }
            }
        }
        return false;
    };

    switch (type) {
        case types.LEAVE_COMMENT:
        case types.UPDATE_COMMENT:
        case types.DELETE_COMMENT:
        case types.GET_COMMENTS:
            return produce(state, draftState => {
                draftState.loading = true;
            });
        case types.LEAVE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                const { comment, isReply, parentComment } = payload;
                if (isReply && parentComment?._id) {
                    // Use recursive helper to find parent comment at any depth
                    findAndAddReply(draftState.selectedComments, parentComment._id, comment);
                } else {
                    draftState.selectedComments?.unshift(comment);
                }

                // Handle this in Feed and in Discovery
            });
        case types.LEAVE_COMMENT_FAIL:
        case types.UPDATE_COMMENT_FAIL:
        case types.DELETE_COMMENT_FAIL:
        case types.GET_COMMENTS_FAIL:
            return produce(state, draftState => {
                draftState.loading = false;
                draftState.errMsg = payload;
            });
        case types.GET_COMMENTS_SUCCESS:
            return produce(state, draftState => {
                const { metadata, data } = payload as any;
                const { limit, page, totalPages } = metadata;
                if (Number(page) > 1) {
                    draftState.selectedComments = [...draftState.selectedComments, ...data];
                    draftState.canLoadMore = page < totalPages;
                } else {
                    draftState.selectedComments = data;
                }
                draftState.loading = false;
                draftState.limit = limit;
            });

        case types.DELETE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                const { documentId } = payload;
                if (payload.payload && payload.payload.parent_comment) {
                    // Use recursive helper to find and remove comment at any depth
                    findAndRemoveComment(draftState.selectedComments, documentId);
                } else {
                    draftState.selectedComments = draftState.selectedComments?.filter(
                        comment => comment._id !== documentId,
                    );
                }
                // Handle for feed and Discovery too

                draftState.loading = false;
            });
        case types.UPDATE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                /** Type of TComment `addittionalPaload` */
                const { documentId, additionalPayload } = payload;
                if (additionalPayload.parent_comment) {
                    // Use recursive helper to find and update comment at any depth
                    findAndUpdateComment(draftState.selectedComments, documentId, comment => {
                        comment.comment = additionalPayload.comment; // TODO: Load the entire object so we can get the updated time
                    });
                } else {
                    const index = draftState.selectedComments?.findIndex(
                        comment => comment._id === additionalPayload._id,
                    );
                    if (index !== -1 && draftState.selectedComments) {
                        draftState.selectedComments[index as unknown as any] = additionalPayload;
                    }
                }
                draftState.loading = false;
            });
        case types.LIKE_COMMENT:
            return produce(state, draftState => {
                const { commentId } = payload;
                draftState.liking = true;
                // Use recursive helper to find and update comment at any depth
                findAndUpdateComment(draftState.selectedComments, commentId, comment => {
                    comment.is_user_liked = true;
                });
            });
        case types.LIKE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                const { data, documentId } = payload;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                findAndUpdateComment(draftState.selectedComments, documentId, comment => {
                    comment.is_user_liked = true;
                    comment.user_like = [data];
                });
            });
        case types.LIKE_COMMENT_FAIL:
            return produce(state, draftState => {
                const { documentId, data } = payload;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                findAndUpdateComment(draftState.selectedComments, documentId, comment => {
                    comment.is_user_liked = false;
                    comment.user_like = [];
                });
                draftState.errMsg = data;
            });
        case types.DELETE_COMMENT_LIKE:
            return produce(state, draftState => {
                const { additionalPayload } = payload;
                draftState.liking = true;
                // Use recursive helper to find and update comment at any depth
                if (additionalPayload?._id) {
                    findAndUpdateComment(draftState.selectedComments, additionalPayload._id, comment => {
                        comment.is_user_liked = false;
                    });
                }
            });
        case types.DELETE_COMMENT_LIKE_SUCCESS:
            return produce(state, draftState => {
                const { additionalPayload } = payload;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                if (additionalPayload?._id) {
                    findAndUpdateComment(draftState.selectedComments, additionalPayload._id, comment => {
                        comment.user_like = [];
                    });
                }
            });
        case types.DELETE_COMMENT_LIKE_FAIL:
            return produce(state, draftState => {
                const { documentId } = payload;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                if (documentId) {
                    findAndUpdateComment(draftState.selectedComments, documentId, comment => {
                        comment.is_user_liked = true;
                    });
                }
            });

        // handle all failed state here when a user is
        case storeType.ENQUEUE_FAILED_ACTIONS:
            return produce(state, (draft: any) => {
                switch (payload.type) {
                    case types.LIKE_COMMENT:
                        return produce(state, draftState => {
                            const { commentId } = payload.payload;
                            draftState.liking = false;
                            // Use recursive helper to find and update comment at any depth
                            findAndUpdateComment(draftState.selectedComments, commentId, comment => {
                                comment.is_user_liked = false;
                            });
                        });
                }
            });

        default:
            return state;
    }
};

export { reducer };
